'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { FileText, ArrowLeft, Download, Printer, Filter, Search, Truck, BarChart3, DollarSign, Star, AlertTriangle, CheckCircle2, Package, TrendingUp, Shield,  } from 'lucide-react';
import { MOCK_SUPPLIERS, formatUGX } from '../../components/suppliersData';
import Icon from '@/components/ui/AppIcon';


// ─── Report Definition ────────────────────────────────────────────────────────

interface ReportDef {
  id: string;
  title: string;
  description: string;
  icon: React.ElementType;
  category: string;
  color: string;
}

const REPORTS: ReportDef[] = [
  { id: 'master-list', title: 'Supplier Master List', description: 'Complete list of all registered suppliers with key details', icon: Truck, category: 'Supplier', color: 'bg-gray-700' },
  { id: 'active', title: 'Active Supplier Report', description: 'All active and preferred suppliers with status', icon: CheckCircle2, category: 'Supplier', color: 'bg-green-500' },
  { id: 'category', title: 'Supplier Category Report', description: 'Suppliers grouped by product/service category', icon: Package, category: 'Supplier', color: 'bg-blue-500' },
  { id: 'spend', title: 'Supplier Spend Report', description: 'Procurement spend by supplier for selected period', icon: DollarSign, category: 'Financial', color: 'bg-orange-500' },
  { id: 'purchase-history', title: 'Supplier Purchase History', description: 'Complete purchase order history per supplier', icon: FileText, category: 'Financial', color: 'bg-teal-500' },
  { id: 'performance', title: 'Supplier Performance Report', description: 'Performance scores, ratings, and KPIs', icon: BarChart3, category: 'Performance', color: 'bg-purple-500' },
  { id: 'evaluation', title: 'Supplier Evaluation Report', description: 'All completed evaluations with scores and comments', icon: Star, category: 'Performance', color: 'bg-amber-500' },
  { id: 'delivery', title: 'Supplier Delivery Report', description: 'On-time delivery rates and delay analysis', icon: Truck, category: 'Performance', color: 'bg-indigo-500' },
  { id: 'quality', title: 'Supplier Quality Report', description: 'Acceptance rates, rejections, and quality scores', icon: CheckCircle2, category: 'Performance', color: 'bg-green-600' },
  { id: 'compliance', title: 'Supplier Compliance Report', description: 'Compliance status and document verification', icon: Shield, category: 'Compliance', color: 'bg-blue-600' },
  { id: 'doc-expiry', title: 'Document Expiry Report', description: 'Supplier documents expiring within selected period', icon: AlertTriangle, category: 'Compliance', color: 'bg-red-500' },
  { id: 'contract', title: 'Supplier Contract Report', description: 'Active, expiring, and expired contracts', icon: FileText, category: 'Compliance', color: 'bg-gray-600' },
  { id: 'incident', title: 'Supplier Incident Report', description: 'All recorded incidents by severity and status', icon: AlertTriangle, category: 'Risk', color: 'bg-red-600' },
  { id: 'invoice', title: 'Supplier Invoice Report', description: 'Invoice status, matching, and payment tracking', icon: FileText, category: 'Financial', color: 'bg-orange-600' },
  { id: 'payment', title: 'Supplier Payment Report', description: 'Payment history and outstanding balances', icon: DollarSign, category: 'Financial', color: 'bg-green-700' },
  { id: 'outstanding-balance', title: 'Outstanding Balance Report', description: 'Suppliers with unpaid or overdue balances', icon: TrendingUp, category: 'Financial', color: 'bg-red-500' },
  { id: 'preferred', title: 'Preferred Supplier Report', description: 'All preferred suppliers with qualification criteria', icon: Star, category: 'Supplier', color: 'bg-purple-600' },
  { id: 'suspended', title: 'Suspended Supplier Report', description: 'Suspended suppliers with reasons and review dates', icon: AlertTriangle, category: 'Risk', color: 'bg-orange-700' },
  { id: 'blacklisted', title: 'Blacklisted Supplier Report', description: 'Blacklisted suppliers with evidence and approvals', icon: Shield, category: 'Risk', color: 'bg-red-700' },
  { id: 'return', title: 'Supplier Return Report', description: 'Goods returns, credit notes, and replacements', icon: Package, category: 'Performance', color: 'bg-amber-600' },
];

const CATEGORIES = ['All', 'Supplier', 'Financial', 'Performance', 'Compliance', 'Risk'];

// ─── Preview Data ─────────────────────────────────────────────────────────────

function getMasterListPreview() {
  return MOCK_SUPPLIERS.map(s => ({
    code: s.supplierCode,
    name: s.name,
    type: s.supplierType,
    category: s.categories[0],
    status: s.status,
    rating: s.rating ?? '—',
    spend: formatUGX(s.totalSpend),
    balance: s.outstandingBalance > 0 ? formatUGX(s.outstandingBalance) : '—',
  }));
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function SupplierReportsContent() {
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('All');
  const [activeReport, setActiveReport] = useState<ReportDef | null>(null);
  const [dateFrom, setDateFrom] = useState('2026-01-01');
  const [dateTo, setDateTo] = useState('2026-09-30');
  const [statusFilter, setStatusFilter] = useState('all');

  const filtered = REPORTS.filter(r => {
    const matchSearch = !search || r.title.toLowerCase().includes(search.toLowerCase());
    const matchCat = category === 'All' || r.category === category;
    return matchSearch && matchCat;
  });

  const grouped = CATEGORIES.slice(1).reduce<Record<string, ReportDef[]>>((acc, cat) => {
    const items = filtered.filter(r => r.category === cat);
    if (items.length > 0) acc[cat] = items;
    return acc;
  }, {});

  const previewData = getMasterListPreview();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center gap-3">
          <Link href="/suppliers" className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
            <ArrowLeft size={16} className="text-gray-500" />
          </Link>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center">
              <FileText size={15} className="text-orange-600" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-gray-900">Supplier Reports</h1>
              <p className="text-xs text-gray-400">Generate and export supplier management reports</p>
            </div>
          </div>
        </div>
      </div>

      <div className="p-6 max-w-screen-2xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* Report List */}
          <div className="lg:col-span-1 space-y-4">
            {/* Filters */}
            <div className="bg-white rounded-2xl border border-gray-200 p-4 space-y-3">
              <div className="relative">
                <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search reports..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-8 pr-3 py-2 text-xs border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-400"
                />
              </div>
              <div className="flex flex-wrap gap-1.5">
                {CATEGORIES.map(cat => (
                  <button
                    key={cat}
                    onClick={() => setCategory(cat)}
                    className={`px-3 py-1.5 text-xs font-semibold rounded-full transition-colors ${category === cat ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>

            {/* Report Groups */}
            {Object.entries(grouped).map(([cat, reports]) => (
              <div key={cat} className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-100">
                  <span className="text-xs font-bold text-gray-600 uppercase tracking-wide">{cat}</span>
                </div>
                <div className="divide-y divide-gray-50">
                  {reports.map(r => {
                    const Icon = r.icon;
                    const isActive = activeReport?.id === r.id;
                    return (
                      <button
                        key={r.id}
                        onClick={() => setActiveReport(r)}
                        className={`w-full flex items-start gap-3 px-4 py-3 text-left transition-colors ${isActive ? 'bg-orange-50' : 'hover:bg-gray-50'}`}
                      >
                        <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${r.color}`}>
                          <Icon size={12} className="text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className={`text-xs font-semibold ${isActive ? 'text-orange-700' : 'text-gray-800'}`}>{r.title}</div>
                          <div className="text-xs text-gray-400 mt-0.5 leading-tight">{r.description}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Report Preview */}
          <div className="lg:col-span-2">
            {!activeReport ? (
              <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center h-full flex flex-col items-center justify-center">
                <FileText size={48} className="text-gray-200 mb-4" />
                <p className="text-sm font-semibold text-gray-500">Select a report to preview</p>
                <p className="text-xs text-gray-400 mt-1">Choose from {REPORTS.length} available supplier reports</p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Report Header */}
                <div className="bg-white rounded-2xl border border-gray-200 p-5">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${activeReport.color}`}>
                        <activeReport.icon size={18} className="text-white" />
                      </div>
                      <div>
                        <h2 className="text-base font-bold text-gray-900">{activeReport.title}</h2>
                        <p className="text-xs text-gray-400 mt-0.5">{activeReport.description}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors">
                        <Printer size={12} />Print
                      </button>
                      <button className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors">
                        <Download size={12} />PDF
                      </button>
                      <button className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-white bg-orange-500 rounded-xl hover:bg-orange-600 transition-colors">
                        <Download size={12} />Excel
                      </button>
                    </div>
                  </div>

                  {/* Filters */}
                  <div className="flex flex-wrap gap-3">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">From</label>
                      <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="px-3 py-1.5 text-xs border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-400" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">To</label>
                      <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="px-3 py-1.5 text-xs border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-400" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Status</label>
                      <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="px-3 py-1.5 text-xs border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-400 bg-white">
                        <option value="all">All Status</option>
                        <option value="active">Active</option>
                        <option value="preferred">Preferred</option>
                        <option value="suspended">Suspended</option>
                      </select>
                    </div>
                    <div className="flex items-end">
                      <button className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-gray-700 rounded-xl hover:bg-gray-800 transition-colors">
                        <Filter size={11} />Generate
                      </button>
                    </div>
                  </div>
                </div>

                {/* Report Summary */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-white rounded-xl border border-gray-200 p-3 text-center">
                    <div className="text-xl font-black text-gray-900">{MOCK_SUPPLIERS.length}</div>
                    <div className="text-xs text-gray-400">Total Suppliers</div>
                  </div>
                  <div className="bg-white rounded-xl border border-gray-200 p-3 text-center">
                    <div className="text-xl font-black text-orange-600">UGX {(MOCK_SUPPLIERS.reduce((s, x) => s + x.totalSpend, 0) / 1000000).toFixed(0)}M</div>
                    <div className="text-xs text-gray-400">Total Spend</div>
                  </div>
                  <div className="bg-white rounded-xl border border-gray-200 p-3 text-center">
                    <div className="text-xl font-black text-gray-900">{dateFrom} — {dateTo}</div>
                    <div className="text-xs text-gray-400">Report Period</div>
                  </div>
                </div>

                {/* Data Preview */}
                <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                  <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
                    <span className="text-xs font-bold text-gray-700">Preview — {previewData.length} records</span>
                    <span className="text-xs text-gray-400">Showing first 5 rows</span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-gray-50 border-b border-gray-100">
                          <th className="text-left px-4 py-2.5 font-semibold text-gray-500">Code</th>
                          <th className="text-left px-4 py-2.5 font-semibold text-gray-500">Supplier Name</th>
                          <th className="text-left px-4 py-2.5 font-semibold text-gray-500">Type</th>
                          <th className="text-left px-4 py-2.5 font-semibold text-gray-500">Category</th>
                          <th className="text-center px-4 py-2.5 font-semibold text-gray-500">Status</th>
                          <th className="text-right px-4 py-2.5 font-semibold text-gray-500">Total Spend</th>
                          <th className="text-right px-4 py-2.5 font-semibold text-gray-500">Balance</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {previewData.slice(0, 5).map((row, i) => (
                          <tr key={i} className="hover:bg-gray-50/50">
                            <td className="px-4 py-2.5 font-semibold text-orange-600">{row.code}</td>
                            <td className="px-4 py-2.5 font-semibold text-gray-800">{row.name}</td>
                            <td className="px-4 py-2.5 text-gray-600">{row.type}</td>
                            <td className="px-4 py-2.5 text-gray-600">{row.category}</td>
                            <td className="px-4 py-2.5 text-center">
                              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${row.status === 'preferred' ? 'bg-purple-50 text-purple-700' : row.status === 'active' ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}`}>
                                {row.status}
                              </span>
                            </td>
                            <td className="px-4 py-2.5 text-right font-semibold text-gray-700">{row.spend}</td>
                            <td className="px-4 py-2.5 text-right text-orange-600 font-semibold">{row.balance}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="px-5 py-3 border-t border-gray-100 text-center">
                    <button className="text-xs font-semibold text-orange-600 hover:underline">
                      Export full report to see all {previewData.length} records →
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
